import { useState } from 'react';
import { Button, FormInstance, Modal, Spin, Typography, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { TextService } from '../../Services/TextService';
import { EmailTemplateService } from '../../Services/EmailTemplateService';
import { EmailTemplateType } from '../../Types/EmailTemplate';
import { WebApiServicesDataTransferObjectsEmailServiceEmailTemplateDesignDto } from '../../Repository/eventbookyapi';

/**
 * The design of an email template form, with only the fields that belong to the chosen design.
 * Undefined when the template has no design.
 */
export const getDesignFromFormValues = (values: any): WebApiServicesDataTransferObjectsEmailServiceEmailTemplateDesignDto | undefined => {
    if (values.enableEmailDesign !== true) {
        return undefined;
    }

    const emailDesignTemplate = values.emailDesignTemplate as EmailTemplateType | undefined;
    const isClassicTemplate = emailDesignTemplate === EmailTemplateType.Classic;
    const isHeaderTemplate = emailDesignTemplate === EmailTemplateType.Header;

    return {
        header: isHeaderTemplate ? values.header : undefined,
        imageUrl: isClassicTemplate ? values.imageUrl : undefined,
        emailDesignTemplate: emailDesignTemplate,
        textColor: isHeaderTemplate ? values.textColor : undefined,
        accentColor: isHeaderTemplate ? values.accentColor : undefined,
    };
}

// Some environments return the preview HTML as a JSON-encoded string.
const normalizePreviewHtml = (content: string) => {
    const trimmedContent = content?.trim();
    if (trimmedContent?.startsWith('"') && trimmedContent.endsWith('"')) {
        try {
            const parsedContent = JSON.parse(trimmedContent);
            if (typeof parsedContent === "string") {
                return parsedContent;
            }
        } catch {
            return content;
        }
    }

    return content;
}

const isHtmlEmpty = (html: string | undefined) => !html || html.replace(/<[^>]*>/g, "").trim().length === 0;

interface EmailTemplatePreviewButtonProps {
    form: FormInstance;
}

/**
 * Shows the email of a template form as it will look, with the text and the design filled in right now.
 */
export const EmailTemplatePreviewButton = (props: EmailTemplatePreviewButtonProps) => {
    const GetText = TextService.GetText;
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [html, setHtml] = useState("");
    const [subject, setSubject] = useState("");

    const onPreview = async () => {
        const values = props.form.getFieldsValue(true);

        if (isHtmlEmpty(values.body)) {
            message.warning(GetText("EmailForm_MessageRequired"));
            return;
        }

        setSubject(values.subject ?? "");
        setHtml("");
        setOpen(true);
        setLoading(true);

        try {
            const result = await EmailTemplateService.PreviewEmail({
                webApiMemberDtoPreviewMailBodyDto: {
                    message: values.body,
                    design: getDesignFromFormValues(values),
                }
            });
            setHtml(normalizePreviewHtml(result) || values.body);
        } catch {
            setOpen(false);
            message.error(GetText("EmailForm_PreviewFailed"));
        } finally {
            setLoading(false);
        }
    }

    return <>
        <Button icon={<EyeOutlined />} onClick={onPreview}>{GetText("EmailForm_Preview")}</Button>

        <Modal
            width={760}
            open={open}
            title={GetText("EmailForm_Preview")}
            onCancel={() => setOpen(false)}
            footer={<Button onClick={() => setOpen(false)}>{GetText("Common_Close")}</Button>}
        >
            {subject && <Typography.Paragraph><b>{GetText("CreateTemplate_Subject")}:</b> {subject}</Typography.Paragraph>}
            {loading || !html
                ? <div style={{ height: "560px", display: "flex", alignItems: "center", justifyContent: "center" }}><Spin /></div>
                : <iframe
                    title={GetText("EmailForm_Preview")}
                    srcDoc={html}
                    sandbox=""
                    style={{ width: "100%", height: "560px", border: "1px solid #f0f0f0", borderRadius: "6px", backgroundColor: "#ffffff" }}
                />}
            <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>{GetText("OneFrontTemplates_PreviewHint")}</Typography.Paragraph>
        </Modal>
    </>;
};

export default EmailTemplatePreviewButton;
